import React from 'react'
import {flushSync} from 'react-dom'

import * as filestack from 'filestack-js'
import TagManager from 'react-gtm-module'
import './photoSelectorOverrideStyle.css'

import {v1 as uuid} from 'uuid'
import {log} from '@/logger'
import {FILESTACK_UP_API_KEY} from '../../constants'
import {useStatusToast} from '../../app/ToastManager'
import {Album} from '../../types'
import {useProcessAlbumPhotosService} from '../../services/useProcessAlbumPhotosService'
import {sessionManager, simplePluralizeWord} from '../../utils'
import {usePreventTabClose} from '../../hooks/usePreventTabClose'
import {useRequiredAuth} from '../../stores/AuthStore'
import {albums as albumsApi} from '../../services/api/albums'
import {useAlbumViewDispatch} from '../../views/AlbumView/AlbumViewManager'

import {
  useAlbumPhotosDispatch,
  useAlbumPhotosState,
} from '../../views/AlbumView/AlbumPhotosManager'
import {usePreventTabNavigation} from '../../hooks/usePreventTabNavigation'

export type OnFileSelected = NonNullable<
  filestack.PickerOptions['onFileSelected']
>

export type OnFileUploadFinished = NonNullable<
  filestack.PickerOptions['onFileUploadFinished']
>
export type OnUploadDone = NonNullable<filestack.PickerOptions['onUploadDone']>
export type OnUploadStarted = NonNullable<
  filestack.PickerOptions['onUploadStarted']
>

export type OnCancel = NonNullable<filestack.PickerOptions['onCancel']>

export type OnFileUploadFailed = NonNullable<
  filestack.PickerOptions['onFileUploadFailed']
>

export type OnFileUploadCancel = NonNullable<
  filestack.PickerOptions['onFileUploadCancel']
>

export type DisplayMode = NonNullable<filestack.PickerOptions['displayMode']>

export type ModalSize = NonNullable<filestack.PickerOptions['modalSize']>

export type OnClose = NonNullable<filestack.PickerOptions['onClose']>

const FS_CONCURRENCY_DEFAULT = 4
const FS_FILE_RETRY = 3
const FS_FILE_RETRY_TIMEOUT = 1000 * 60 * 5 // 5 minutes
const FS_FILE_INTELLIGENT_CHUCK_SIZE = 1 * 1024 * 1024 // 1 MB
const FS_MAX_FILES_COUNT = 2000
const FS_MAX_FILE_SIZE = 1024 * 1024 * 20 // 20971520 === 20MB
const STORETO_S3_SUBFOLDER = 'upload/'

// Filestack upload source identifiers. These values are part of the Filestack
// SDK contract; keep them here as the single source of truth for the picker's
// `fromSources` list below.
const FS_SOURCES = {
  localFileSystem: 'local_file_system',
  googleDrive: 'googledrive',
  googlePhotos: 'googlephotos',
  instagram: 'instagram',
  facebook: 'facebook',
  dropbox: 'dropbox',
} as const

export const transformPhotoNameForProcessing = (origName: string): string => {
  const subfolderRegex = new RegExp(`^${STORETO_S3_SUBFOLDER}`, 'g')

  return origName.replace(subfolderRegex, '')
}

const fileStackClient = filestack.init(FILESTACK_UP_API_KEY, {
  sessionCache: true,
})

const defaultPickerOptions: filestack.PickerOptions = {
  storeTo: {
    location: 's3',
    path: `/${STORETO_S3_SUBFOLDER}`,
  },
  displayMode: filestack.PickerDisplayMode.overlay,
  // Google Drive and Google Photos are disabled: both send the user through an
  // OAuth consent flow that fails with permission/unverified-app warnings, so
  // camps hit a dead end whenever they pick either source. Left commented (not
  // deleted) so re-enabling is a one-line change once the OAuth setup is fixed.
  fromSources: [
    FS_SOURCES.localFileSystem,
    // FS_SOURCES.googleDrive,
    // FS_SOURCES.googlePhotos,
    FS_SOURCES.instagram,
    FS_SOURCES.facebook,
    FS_SOURCES.dropbox,
  ],
  accept: ['image/jpeg', 'image/png', 'image/heic', 'image/heif'],
  concurrency: FS_CONCURRENCY_DEFAULT,
  uploadConfig: {
    retry: FS_FILE_RETRY,
    timeout: FS_FILE_RETRY_TIMEOUT,
    intelligent: true,
    intelligentChunkSize: FS_FILE_INTELLIGENT_CHUCK_SIZE,
  },
  customText: {
    'Select Files to Upload': 'Select Photos to Upload',
    'or Drag and Drop, Copy and Paste Files':
      'Drag and drop or browse to select photos. Images must be JPG or PNG file formats and less than 20mb.',
    'Selected Files': 'Selected Photos',
    Images: 'Photos',
    'Deselect All': 'Remove All',
    'Upload more': 'Add Photos',
    Filter: 'Search',
  },
  allowManualRetry: true, // Prevent modal close on upload failure and allow users to retry.
  disableTransformer: true,
  maxFiles: FS_MAX_FILES_COUNT,
  maxSize: FS_MAX_FILE_SIZE,
  uploadInBackground: false,
}

interface Props {
  albumID: Album['ID']
  container: string
  displayMode?: DisplayMode
  openPickerByDefault?: boolean
  modalSize?: ModalSize
  onDone?: () => void
}

export const PhotoSelector: React.FC<React.PropsWithChildren<Props>> = ({
  albumID,
  container,
  displayMode = filestack.PickerDisplayMode.overlay,
  openPickerByDefault = false,
  modalSize,
  onDone,
}) => {
  const {toast} = useStatusToast()
  const {processPhoto} = useProcessAlbumPhotosService({albumID})
  const {client} = useRequiredAuth()
  const albumViewDispatch = useAlbumViewDispatch()
  const {resetPhotos, status} = useAlbumPhotosState()
  const albumPhotosDispatch = useAlbumPhotosDispatch()

  // const [albumUploadSessionID, setAlbumUploadSessionID] = React.useState(uuid())
  const albumUploadSessionID = React.useRef<string>()
  const pickerInstance = React.useRef<filestack.PickerInstance>()
  const canceledFiles = React.useRef<filestack.PickerFileMetadata[]>([])
  const notFoundFilesCount = React.useRef(0)
  // Tracks whether the component is still mounted, so the async picker-open
  // failure handler below can tell an expected unmount-time rejection (user
  // navigated away mid-load) from a real one. See the open effect (AB#21200).
  const isMountedRef = React.useRef(true)

  const isOpen = openPickerByDefault
  usePreventTabClose(isOpen)
  usePreventTabNavigation(status === 'uploading')

  const handleUploadStarted = React.useCallback<OnUploadStarted>(
    (files) => {
      log.breadcrumb('FS Upload Started', files)
      log.breadcrumb('Creating AlbumUploadSession', {
        albumID,
      })

      const sendAlbumUploadSession = async (): Promise<void> => {
        const sessionID = uuid()

        const response = await albumsApi.createAlbumUploadSession({
          ID: sessionID,
          albumID,
          clientID: client.ID,
          photoFileNames: files.map((uploadedFile) => uploadedFile.filename),
        })

        if (response.result === 'failure') {
          toast.error(
            'Automatic album sorting failed. Go to Manage Album > Sort Photo Order after the upload completes to select sorting method.',
          )

          log.error(new Error(JSON.stringify(response.error)))
        } else {
          albumUploadSessionID.current = sessionID
        }
      }

      sendAlbumUploadSession()

      albumPhotosDispatch({type: 'START_UPLOADING'})
    },
    [albumID, client, albumPhotosDispatch, toast],
  )

  const handleOnFileUploadFinished = React.useCallback<OnFileUploadFinished>(
    async (file) => {
      let fileKey = file.key

      if (fileKey) {
        // The image processor doesn't transform HEIC/HEIF files to JPG so we will do in the client
        if (file.mimetype === 'image/heic' || file.mimetype === 'image/heif') {
          // Grab the Filestack handle of the uploaded file which is like its unique ID for filestack's system
          const {handle} = file

          //  Create a transformation URL that converts the file to JPG:
          //  output=format:jpg is the key transformation
          const transformedUrl = `https://cdn.filestackcontent.com/output=format:jpg/${handle}`

          try {
            // Store the transformed file directly to S3 via Filestack:
            const storeOptions = {
              filename: `${file.filename}_converted.jpg`,
              path: defaultPickerOptions.storeTo?.path,
            }
            const storeResponse = (await fileStackClient.storeURL(
              transformedUrl,
              storeOptions,
            )) as {key: string} // Filestack return type is just a broad Object

            if (!storeResponse.key) {
              throw new Error(
                `Filestack did not return a key for ${JSON.stringify(
                  storeResponse,
                )}`,
              )
            }

            fileKey = storeResponse.key

            TagManager.dataLayer({
              dataLayer: {
                event: 'image_conversion',
                conversion_type: file.mimetype,
              },
            })
          } catch (err) {
            if (err instanceof Error) {
              log.error(err)
            } else {
              log.error(new Error(JSON.stringify(err)))
            }
          }
        }

        // Let the image processor know that the file is ready to be processed and where it is located
        processPhoto({
          fileName: transformPhotoNameForProcessing(fileKey),
          uploadSessionID: albumUploadSessionID.current, // sessionID might be undefined still because the create request is either still in flight or errorer. This is preferred to telling a lie about the sessionID number. We have plans to prevent making the photoProcess request until the sessionID is known but do not want to change the photo process flow too drastically during summer.
        })
      }
    },
    [processPhoto],
  )

  const handleOnFileUploadFailed = React.useCallback<OnFileUploadFailed>(
    async (file, error) => {
      // File was canceled by user. Don't Log as error
      if (canceledFiles.current.find((curr) => curr.filename)) {
        return
      }

      if (!(error instanceof Error)) {
        // Assuming this is because the file was not found and it was already logged in upload.error
        if (notFoundFilesCount.current > 0) {
          notFoundFilesCount.current -= 1
          return
        }

        log.error(new Error('File Upload Failed'), (event) => {
          event.addMetadata('photo details', file)
          event.addMetadata('original error instance', error)
        })
      } else {
        log.error(error, (event) => {
          event.addMetadata('photo details', file)
        })
      }
    },
    [],
  )

  const handleOnUploadDone = React.useCallback<OnUploadDone>(
    (res) => {
      log.breadcrumb('FS Upload Done')

      // resetPhotos() flips status off 'uploading', which tears down the
      // usePreventTabNavigation history.block below via its effect cleanup.
      // The redirect in onDone() (history.goBack) runs synchronously right
      // after this call, so the block MUST be gone before then or the
      // programmatic redirect trips React Router's "Are you sure you want to
      // leave this page?" prompt. Under React 17 the dispatch inside this
      // (non-React) Filestack callback flushed synchronously, so the cleanup
      // ran before goBack. React 18's automatic batching defers that
      // re-render/cleanup past the synchronous goBack, reintroducing the
      // prompt — flushSync restores the ordering by flushing the state update
      // and its effect cleanup before we navigate.
      flushSync(() => {
        resetPhotos()
      })

      canceledFiles.current = []

      if (res.filesUploaded.length > 0) {
        toast.success(
          `${simplePluralizeWord('photo', res.filesUploaded.length)} uploaded.`,
        )
      }

      if (res.filesFailed.length > 0) {
        toast.error(
          `${simplePluralizeWord(
            'photo',
            res.filesFailed.length,
          )} failed to upload`,
        )
      }

      if (onDone) {
        onDone()
      }
    },
    [resetPhotos, onDone, toast],
  )

  const handleOnCancel = React.useCallback<OnCancel>(() => {
    log.breadcrumb('User Cancelled Upload')

    canceledFiles.current = []
  }, [])

  const handleOnFileSelected = React.useCallback<OnFileSelected>((file) => {
    const fileNameValidation = /^[A-Za-z0-9-.()_\s]+$/

    if (!fileNameValidation.test(file.filename)) {
      throw new Error(`File name ${file.filename} contains invalid characters.`)
    }

    if (file.size && file.size < 1_000) {
      throw new Error(`File name ${file.filename} is too small.`)
    }
  }, [])

  const handleOnFileUploadCancel = React.useCallback<OnFileUploadCancel>(
    (file) => {
      log.breadcrumb('canceled file', {filename: file.filename})

      // File did not get uploaded to filestack
      if (!file.url) {
        // onFileUploadCancel(file)
        canceledFiles.current.push(file)
      }
    },
    [],
  )

  const handleOnClose = React.useCallback<OnClose>(() => {
    albumViewDispatch({type: 'SET_VIEW_MODE', payload: 'initial'})
  }, [albumViewDispatch])

  const modalSizeProp = React.useMemo(
    () => (modalSize ? {modalSize} : {}),
    [modalSize],
  )

  const pickerOptions: filestack.PickerOptions = React.useMemo(
    () => ({
      ...defaultPickerOptions,
      container,
      onUploadDone: handleOnUploadDone,
      onFileUploadFailed: handleOnFileUploadFailed,
      onFileUploadFinished: handleOnFileUploadFinished,
      onFileSelected: handleOnFileSelected,
      onUploadStarted: handleUploadStarted,
      onFileUploadCancel: handleOnFileUploadCancel,
      onClose: handleOnClose,
      onCancel: handleOnCancel,
      displayMode,
      ...modalSizeProp,
    }),
    [
      handleOnCancel,
      handleOnFileUploadFailed,
      handleOnFileUploadFinished,
      handleOnFileSelected,
      handleOnUploadDone,
      handleUploadStarted,
      handleOnFileUploadCancel,
      handleOnClose,
      container,
      displayMode,
      modalSizeProp,
    ],
  )

  // An album upload can easily outlast the 30-minute idle window (2,000 photos
  // over a camp's uplink) while producing nothing the idle timer counts as
  // activity: the files stream browser → Filestack → S3, and the user has no
  // reason to touch the page. The timer would then expire the session and
  // hard-redirect to /login?expired=1 with the upload still running. Hold the
  // session open for as long as the upload is in progress (AB#24880).
  //
  // A hold, not a heartbeat. This was once a once-a-minute `sessionManager.reset()`
  // interval, which only worked while the browser let that interval run on time —
  // and a long upload is usually in a backgrounded tab, where timers are throttled
  // hardest and the idle timer now measures real elapsed time rather than ticks.
  // A hold is read before the clock is, so no timer of ours has to win a race.
  React.useEffect(() => {
    if (status !== 'uploading') {
      return undefined
    }

    return sessionManager.hold()
  }, [status])

  // Subscribe/Unsubscribe to different FS error listener
  React.useEffect(() => {
    fileStackClient.on('upload.error', (filestackError) => {
      // Do not log error when it was a user canceled file
      if (
        filestackError?.message === 'FS-Cannot upload file part' &&
        canceledFiles.current.length > 0
      ) {
        return
      }

      // Filestack gave a real error to log
      if (
        filestackError?.currentTarget?.error &&
        filestackError.currentTarget.error instanceof Error
      ) {
        const {
          currentTarget: {error},
        } = filestackError
        // file was moved before upload, so only info log and supress fileFail error log
        if (error.name === 'NotFoundError') {
          log.info(error)
          notFoundFilesCount.current += 1
        } else {
          log.error(error)
        }
        return
      }

      // No idea what this error is
      log.error(new Error('FileStack upload.error'), (event) => {
        event.addMetadata('filestackError', filestackError)
      })
    })

    return (): void => {
      fileStackClient.off('upload.error')
    }
  }, [])

  // Track mount state for the async picker-open failure handler (AB#21200).
  // The `= true` is NOT redundant with the useRef(true) default: under
  // StrictMode the mount→cleanup→mount cycle runs the cleanup (→ false) and
  // then this body again, so the body must restore true on every (re)mount.
  // Caveat: in StrictMode's dev-only mount→cleanup→remount cycle the remount
  // synchronously restores isMountedRef.current = true before the open()
  // rejection microtask runs, so a first-mount rejection is misclassified the
  // other way — as a real error (spurious log.error), not an after-unmount
  // breadcrumb. Production single-mount is unaffected.
  React.useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  // Get PickerJS from FileStack
  React.useEffect(() => {
    try {
      // The picker is instantiated exactly once and never rebuilt.
      if (!pickerInstance.current) {
        pickerInstance.current = fileStackClient.picker(pickerOptions)
      }
    } catch (e) {
      if (e instanceof Error) {
        log.error(e)
      }
    }
  }, [pickerOptions])

  // Toggle Open/Close of uploader modal.
  //
  // React runs effects in source-declaration order, so the build effect above
  // runs first in the same commit and pickerInstance.current is ready by the
  // time this effect runs on mount.
  React.useEffect(() => {
    const picker = pickerInstance.current
    if (isOpen && picker) {
      // open() binds the container inside an awaited promise, so its rejection
      // escapes a synchronous try/catch. Handle it via .catch and classify on
      // isMountedRef (see its declaration and AB#21200).
      try {
        Promise.resolve(picker.open()).catch((e: unknown) => {
          if (!isMountedRef.current) {
            log.breadcrumb(
              'Filestack picker open() rejected after unmount; ignoring',
            )
            return
          }
          if (e instanceof Error) {
            log.error(e)
          } else {
            log.error(new Error(JSON.stringify(e)))
          }
        })
      } catch (e) {
        if (e instanceof Error) {
          log.error(e)
        }
      }
    }

    return (): void => {
      const currentPicker = pickerInstance.current
      if (!currentPicker) {
        return
      }
      // pickerInstance.current is intentionally left set (not reset to
      // undefined) — clearing it here previously broke opening/closing the
      // modal multiple times.
      //
      // cancel()/close() await the SAME internal init promise open() does
      // (filestack-js binds the container during the eager load kicked off by
      // picker()), so on an unmount mid-init they reject asynchronously with the
      // same "#divContainer not found" error. The previous synchronous
      // try/catch could NOT catch a promise rejection — the exact trap called
      // out on open() above (AB#21200) — so the teardown rejection escaped and
      // was reported as an unhandled error (AB#21629). Handle each via .catch; a
      // teardown-time rejection is always the expected unmount-mid-init case, so
      // record a breadcrumb rather than counting it as an error. A synchronous
      // throw before any promise is treated the same way (see teardown() below).
      const ignoreTeardownRejection = (e: unknown): void => {
        log.breadcrumb(
          'Filestack picker cancel()/close() rejected during teardown; ignoring',
          {error: e instanceof Error ? e.message : String(e)},
        )
      }
      // Run each teardown call independently so a synchronous throw from
      // cancel() does not skip close() (which would leave the picker
      // half-torn-down). A synchronous throw at teardown time is the same
      // expected unmount-mid-init case as an async rejection, so route it
      // through ignoreTeardownRejection too — a breadcrumb, not a log.error,
      // and never silently dropped for a non-Error value.
      const teardown = (call: () => unknown): void => {
        try {
          Promise.resolve(call()).catch(ignoreTeardownRejection)
        } catch (e) {
          ignoreTeardownRejection(e)
        }
      }
      teardown(() => currentPicker.cancel())
      teardown(() => currentPicker.close())
    }
  }, [isOpen])

  return null
}
